import React, { ChangeEvent, useEffect } from 'react';

import {
  CloseIcon,
  Divider,
  EqualIcon,
  Label,
  Select,
  Text,
  type TypeSelectOption,
  ValidationMessage,
} from '@thefarmersfront/kitchen';

import { useShallow } from 'zustand/react/shallow';

import { NumberUnitInput } from '~/components/common';

import { useProductModalOptions } from '~/hooks/product';
import { useProductStore, useUserInfoStore } from '~/store';
import { formatNumber, validateProductVolumeField, validateSingleProductField } from '~/utils';

import { PRODUCT_LIMITS } from '~/constants/product';

import type { TypeProductModalRequiredInputState } from '~/types/product.types';

import styles from './ProductRegisterModalProductInfoSectionMeasurement.module.scss';

const ProductRegisterModalInfoSectionMeasurement = () => {
  const hasPermission = useUserInfoStore((state) => state.hasPermission);
  const hasPermissionFBK = hasPermission([{ ownerType: 'FBK' }]);

  const productModalOptions = useProductModalOptions();

  const [
    productRegisterModalValue,
    setProductRegisterModalValue,
    productModalRequiredValueStatus,
    setProductModalRequiredValueStatus,
    resetProductModalRequiredValueStatus,
    setFocusFieldRefs,
  ] = useProductStore(
    useShallow((state) => [
      state.productRegisterModalValue,
      state.setProductRegisterModalValue,
      state.productModalRequiredValueStatus,
      state.setProductModalRequiredValueStatus,
      state.resetProductModalRequiredValueStatus,
      state.setFocusFieldRefs,
    ])
  );

  useEffect(() => {
    let updatedCbm;

    if (productRegisterModalValue.height && productRegisterModalValue.length && productRegisterModalValue.width) {
      updatedCbm =
        productRegisterModalValue.height * productRegisterModalValue.length * productRegisterModalValue.width;
    } else {
      updatedCbm = null;
    }

    setProductRegisterModalValue({
      ...productRegisterModalValue,
      cbm: updatedCbm,
    });

    const validationResult = validateProductVolumeField(updatedCbm, {
      length: productModalRequiredValueStatus.length,
      width: productModalRequiredValueStatus.width,
      height: productModalRequiredValueStatus.height,
    });

    setProductModalRequiredValueStatus({
      ...productModalRequiredValueStatus,
      cbm: validationResult,
    });
  }, [
    productRegisterModalValue.height,
    productRegisterModalValue.length,
    productRegisterModalValue.width,
    productModalRequiredValueStatus.length,
    productModalRequiredValueStatus.width,
    productModalRequiredValueStatus.height,
  ]);

  useEffect(() => {
    let updatedBoxCbm;

    if (
      productRegisterModalValue.boxHeight &&
      productRegisterModalValue.boxLength &&
      productRegisterModalValue.boxWidth
    ) {
      updatedBoxCbm =
        productRegisterModalValue.boxHeight * productRegisterModalValue.boxLength * productRegisterModalValue.boxWidth;
    } else {
      updatedBoxCbm = null;
    }

    setProductRegisterModalValue({
      ...productRegisterModalValue,
      boxCbm: updatedBoxCbm,
    });

    const validationResult = validateProductVolumeField(updatedBoxCbm, {
      length: productModalRequiredValueStatus.boxLength,
      width: productModalRequiredValueStatus.boxWidth,
      height: productModalRequiredValueStatus.boxHeight,
    });

    setProductModalRequiredValueStatus({
      ...productModalRequiredValueStatus,
      boxCbm: validationResult,
    });
  }, [
    productRegisterModalValue.boxHeight,
    productRegisterModalValue.boxLength,
    productRegisterModalValue.boxWidth,
    productModalRequiredValueStatus.boxLength,
    productModalRequiredValueStatus.boxWidth,
    productModalRequiredValueStatus.boxHeight,
  ]);

  const handleClickRequiredInput = (e: React.MouseEvent) => {
    resetProductModalRequiredValueStatus(e.currentTarget.id as keyof TypeProductModalRequiredInputState);
  };

  const handleChangeUnitSelect = (_: unknown, value: TypeSelectOption) => {
    setProductRegisterModalValue({
      ...productRegisterModalValue,
      unit: value,
    });

    resetProductModalRequiredValueStatus('unit');
  };

  const handleChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
    const id = e.currentTarget.id as keyof TypeProductModalRequiredInputState;
    const value = e.target.value === '' ? null : e.target.value;

    setProductRegisterModalValue({
      ...productRegisterModalValue,
      [id]: value,
    });

    const validationResult = validateSingleProductField(id, value);

    setProductModalRequiredValueStatus({
      ...productModalRequiredValueStatus,
      [id]: validationResult,
    });
  };

  // 필드 상태와 부피 상태를 조합해 최종 상태 반환
  const getVolumeFieldStatus = (fieldKey: string, volumeKey: string): 'error' | 'default' => {
    return productModalRequiredValueStatus[fieldKey as keyof TypeProductModalRequiredInputState]?.status === 'error' ||
      productModalRequiredValueStatus[volumeKey as keyof TypeProductModalRequiredInputState]?.status === 'error'
      ? 'error'
      : 'default';
  };

  return (
    <>
      <div className={styles['item-row']}>
        <Label className={styles['label']} title="단위" required />
        <div className={styles['input-wrapper']}>
          <Select
            ref={(el) => setFocusFieldRefs('unit', el)}
            id="unit"
            size="small"
            options={productModalOptions?.unit ?? []}
            value={productRegisterModalValue.unit}
            status={productModalRequiredValueStatus.unit.status}
            validationMessage={productModalRequiredValueStatus.unit.message}
            onChange={handleChangeUnitSelect}
          />
        </div>
      </div>

      <div className={styles['item-row']}>
        <Label className={styles['label']} title="개당 중량" required />
        <div className={styles['weight-input-wrapper']}>
          <div className={styles['weight-input-item']}>
            <Text className={styles['title']} size="large">
              순중량
            </Text>
            <NumberUnitInput
              ref={(el) => setFocusFieldRefs('netWeight', el)}
              id="netWeight"
              className={styles['input']}
              value={productRegisterModalValue.netWeight ?? ''}
              size="small"
              unit="g"
              placeholder="숫자만 입력"
              status={productModalRequiredValueStatus.netWeight.status}
              validationMessage={productModalRequiredValueStatus.netWeight.message}
              onClick={handleClickRequiredInput}
              onChange={handleChangeInput}
            />
          </div>
          <Divider orientation="vertical" className={styles['divider']} />
          <div className={styles['weight-input-item']}>
            <Text className={styles['title']} size="large">
              총중량
            </Text>
            <NumberUnitInput
              ref={(el) => setFocusFieldRefs('grossWeight', el)}
              id="grossWeight"
              className={styles['input']}
              value={productRegisterModalValue.grossWeight ?? ''}
              size="small"
              unit="g"
              placeholder="숫자만 입력"
              status={productModalRequiredValueStatus.grossWeight.status}
              validationMessage={productModalRequiredValueStatus.grossWeight.message}
              onClick={handleClickRequiredInput}
              onChange={handleChangeInput}
            />
          </div>
        </div>
      </div>

      <div className={styles['item-row']}>
        <Label className={styles['label']} title="개당 부피" required={hasPermissionFBK} />
        <div className={styles['volume-input-container']}>
          <div className={styles['volume-input-wrapper']}>
            <div className={styles['volume-input-item']}>
              <Text className={styles['title']} size="large">
                길이
              </Text>
              <NumberUnitInput
                ref={(el) => setFocusFieldRefs('length', el)}
                id="length"
                className={styles['input']}
                value={productRegisterModalValue.length ?? ''}
                size="small"
                unit="mm"
                placeholder="숫자만 입력"
                status={getVolumeFieldStatus('length', 'cbm')}
                validationMessage={
                  productModalRequiredValueStatus.length?.status === 'error'
                    ? productModalRequiredValueStatus.length?.message
                    : ''
                }
                onClick={handleClickRequiredInput}
                onChange={handleChangeInput}
              />
            </div>
            <CloseIcon size={16} color="gray400" className={styles['icon']} />
            <div className={styles['volume-input-item']}>
              <Text className={styles['title']} size="large">
                폭
              </Text>
              <NumberUnitInput
                ref={(el) => setFocusFieldRefs('width', el)}
                id="width"
                className={styles['input']}
                value={productRegisterModalValue.width ?? ''}
                size="small"
                unit="mm"
                placeholder="숫자만 입력"
                status={getVolumeFieldStatus('width', 'cbm')}
                validationMessage={
                  productModalRequiredValueStatus.width?.status === 'error'
                    ? productModalRequiredValueStatus.width?.message
                    : ''
                }
                onClick={handleClickRequiredInput}
                onChange={handleChangeInput}
              />
            </div>
            <CloseIcon size={16} color="gray400" className={styles['icon']} />
            <div className={styles['volume-input-item']}>
              <Text className={styles['title']} size="large">
                높이
              </Text>
              <NumberUnitInput
                ref={(el) => setFocusFieldRefs('height', el)}
                id="height"
                className={styles['input']}
                value={productRegisterModalValue.height ?? ''}
                size="small"
                unit="mm"
                placeholder="숫자만 입력"
                status={getVolumeFieldStatus('height', 'cbm')}
                validationMessage={
                  productModalRequiredValueStatus.height?.status === 'error'
                    ? productModalRequiredValueStatus.height?.message
                    : ''
                }
                onClick={handleClickRequiredInput}
                onChange={handleChangeInput}
              />
            </div>
            <EqualIcon size={16} color="gray400" className={styles['icon']} />
            <div className={styles['volume-input-item']}>
              <Text className={styles['title']} size="large">
                부피
              </Text>
              <NumberUnitInput
                ref={(el) => setFocusFieldRefs('cbm', el)}
                id="cbm"
                className={styles['result-input']}
                value={productRegisterModalValue.cbm ?? ''}
                disabled
                size="small"
                unit="mm³"
              />
            </div>
          </div>
          {productModalRequiredValueStatus.cbm?.status === 'error' && (
            <ValidationMessage
              className={styles['volume-validation-message']}
              message={productModalRequiredValueStatus.cbm?.message ?? ''}
            />
          )}
        </div>
      </div>

      <div className={styles['item-row']}>
        <Label className={styles['label']} title="박스당 입수" required />
        <div className={styles['input-wrapper']}>
          <NumberUnitInput
            ref={(el) => setFocusFieldRefs('boxPerQuantity', el)}
            id="boxPerQuantity"
            className={styles['input']}
            value={productRegisterModalValue.boxPerQuantity ?? ''}
            size="small"
            name="countPerBox"
            placeholder={`${formatNumber(PRODUCT_LIMITS.INPUT_VALUES.BOX_PER_QTY)} 이하 숫자만 입력`}
            status={productModalRequiredValueStatus.boxPerQuantity.status}
            validationMessage={productModalRequiredValueStatus.boxPerQuantity.message}
            onClick={handleClickRequiredInput}
            onChange={handleChangeInput}
          />
        </div>
      </div>

      <div className={styles['item-row']}>
        <Label className={styles['label']} title="박스당 중량" required />
        <div className={styles['weight-input-wrapper']}>
          <div className={styles['weight-input-item']}>
            <Text className={styles['title']} size="large">
              순중량
            </Text>
            <NumberUnitInput
              ref={(el) => setFocusFieldRefs('boxNetWeight', el)}
              id="boxNetWeight"
              className={styles['input']}
              value={productRegisterModalValue.boxNetWeight ?? ''}
              size="small"
              unit="g"
              placeholder="숫자만 입력"
              status={productModalRequiredValueStatus.boxNetWeight.status}
              validationMessage={productModalRequiredValueStatus.boxNetWeight.message}
              onClick={handleClickRequiredInput}
              onChange={handleChangeInput}
            />
          </div>
          <Divider orientation="vertical" className={styles['divider']} />
          <div className={styles['weight-input-item']}>
            <Text className={styles['title']} size="large">
              총중량
            </Text>
            <NumberUnitInput
              ref={(el) => setFocusFieldRefs('boxGrossWeight', el)}
              id="boxGrossWeight"
              className={styles['input']}
              value={productRegisterModalValue.boxGrossWeight ?? ''}
              size="small"
              unit="g"
              placeholder="숫자만 입력"
              status={productModalRequiredValueStatus.boxGrossWeight.status}
              validationMessage={productModalRequiredValueStatus.boxGrossWeight.message}
              onClick={handleClickRequiredInput}
              onChange={handleChangeInput}
            />
          </div>
        </div>
      </div>

      <div className={styles['item-row']}>
        <Label className={styles['label']} title="박스당 부피" required={hasPermissionFBK} />
        <div className={styles['volume-input-container']}>
          <div className={styles['volume-input-wrapper']}>
            <div className={styles['volume-input-item']}>
              <Text className={styles['title']} size="large">
                길이
              </Text>
              <NumberUnitInput
                ref={(el) => setFocusFieldRefs('boxLength', el)}
                id="boxLength"
                className={styles['input']}
                value={productRegisterModalValue.boxLength ?? ''}
                size="small"
                unit="mm"
                placeholder="숫자만 입력"
                status={getVolumeFieldStatus('boxLength', 'boxCbm')}
                validationMessage={
                  productModalRequiredValueStatus.boxLength?.status === 'error'
                    ? productModalRequiredValueStatus.boxLength?.message
                    : ''
                }
                onClick={handleClickRequiredInput}
                onChange={handleChangeInput}
              />
            </div>
            <CloseIcon size={16} color="gray400" className={styles['icon']} />
            <div className={styles['volume-input-item']}>
              <Text className={styles['title']} size="large">
                폭
              </Text>
              <NumberUnitInput
                ref={(el) => setFocusFieldRefs('boxWidth', el)}
                id="boxWidth"
                className={styles['input']}
                value={productRegisterModalValue.boxWidth ?? ''}
                size="small"
                unit="mm"
                placeholder="숫자만 입력"
                status={getVolumeFieldStatus('boxWidth', 'boxCbm')}
                validationMessage={
                  productModalRequiredValueStatus.boxWidth?.status === 'error'
                    ? productModalRequiredValueStatus.boxWidth?.message
                    : ''
                }
                onClick={handleClickRequiredInput}
                onChange={handleChangeInput}
              />
            </div>
            <CloseIcon size={16} color="gray400" className={styles['icon']} />
            <div className={styles['volume-input-item']}>
              <Text className={styles['title']} size="large">
                높이
              </Text>
              <NumberUnitInput
                ref={(el) => setFocusFieldRefs('boxHeight', el)}
                id="boxHeight"
                className={styles['input']}
                value={productRegisterModalValue.boxHeight ?? ''}
                size="small"
                unit="mm"
                placeholder="숫자만 입력"
                status={getVolumeFieldStatus('boxHeight', 'boxCbm')}
                validationMessage={
                  productModalRequiredValueStatus.boxHeight?.status === 'error'
                    ? productModalRequiredValueStatus.boxHeight?.message
                    : ''
                }
                onClick={handleClickRequiredInput}
                onChange={handleChangeInput}
              />
            </div>
            <EqualIcon size={16} color="gray400" className={styles['icon']} />
            <div className={styles['volume-input-item']}>
              <Text className={styles['title']} size="large">
                부피
              </Text>
              <NumberUnitInput
                ref={(el) => setFocusFieldRefs('boxCbm', el)}
                id="boxCbm"
                className={styles['result-input']}
                value={productRegisterModalValue.boxCbm ?? ''}
                disabled
                size="small"
                unit="mm³"
              />
            </div>
          </div>
          {productModalRequiredValueStatus.boxCbm?.status === 'error' && (
            <ValidationMessage
              className={styles['volume-validation-message']}
              message={productModalRequiredValueStatus.boxCbm?.message ?? ''}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default ProductRegisterModalInfoSectionMeasurement;
